import { gql } from "@apollo/client";

// ==========================================
// BILL QUERIES
// ==========================================

/** Get all bills for the current tenant (used in Bills list page) */
export const GET_ALL_BILLS = gql`
  query GetAllBills {
    bills {
      id
      status
      isPaid
      issuedBy
      currency
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      createdAt
      updatedAt
      tenantId
      clientId
      projectId
      paymentMethod
      tokensApplied
      tokenDiscountAmount
    }
  }
`;

/** Get a single bill by ID (authenticated) */
export const GET_BILL = gql`
  query GetBillById($id: ID!) {
    bill(id: $id) {
      id
      terms {
        paymentTerms
        paymentTermsSource
        dueDate
        dueDateSource
        dueDaysAfterSending
      }
      isPaid
      status
      paymentMethod
      currency
      acceptCardPayment
      acceptCryptoPayment
      acceptTokenPayment
      tokenDiscountAmount
      tokensApplied
      bankAccountId
      projectId
      clientId
      companyId
      billToCompanyId
      issuedBy
      clientEditLocked
      clientSnapshot {
        capturedAt
      }
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      tenantId
      createdAt
      updatedAt
    }
  }
`;

/** Get a single bill by ID for payment success page (minimal fields) */
export const GET_BILL_PAYMENT_SUCCESS = gql`
  query GetBillById($id: ID!) {
    bill(id: $id) {
      id
      isPaid
      status
      paymentMethod
      currency
      projectId
      totalAmount
      lineItems {
        id
        description
        amount
      }
      createdAt
      updatedAt
    }
  }
`;

/** Public bill query (no authentication required) */
export const PUBLIC_GET_BILL = gql`
  query PublicGetBillById($id: ID!) {
    publicBill(id: $id) {
      id
      terms {
        paymentTerms
        paymentTermsSource
        dueDate
        dueDateSource
        dueDaysAfterSending
      }
      isPaid
      status
      paymentMethod
      currency
      acceptBankTransfer
      acceptCardPayment
      acceptCryptoPayment
      acceptTokenPayment
      tokenDiscountAmount
      tokensApplied
      bankAccountId
      projectId
      clientId
      companyId
      billToCompanyId
      issuedBy
      clientEditLocked
      clientSnapshot {
        capturedAt
      }
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      tenantId
      isSubscriptionBill
      subscriptionId
      billingPeriod
      paymentEvents {
        method
        status
        amount
        paidAt
        reference
        details
        # Admin-only by construction: the backend OMITS this field for anyone
        # without BILLS_ADMIN, so a logged-out payer never receives it and there
        # is nothing to hide in the browser. Hide-don't-disable.
        stripeDashboardUrl
        screenshotUrl
        confirmedAt
        confirmedBy
      }
      createdAt
      updatedAt
    }
  }
`;

// ==========================================
// CLIENT QUERIES (bill-related)
// ==========================================

/** Get client details by bill ID (authenticated) */
export const GET_CLIENT_BY_BILL = gql`
  query GetClientDetailsByBillId($billId: ID!) {
    getClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

/** Get client details by bill ID for payment success (minimal fields) */
export const GET_CLIENT_BY_BILL_PAYMENT_SUCCESS = gql`
  query GetClientDetailsByBillId($billId: ID!) {
    getClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      businessName
    }
  }
`;

/** Public client query by bill ID (no authentication required) */
export const PUBLIC_GET_CLIENT_BY_BILL = gql`
  query PublicGetClientDetailsByBillId($billId: ID!) {
    publicGetClientDetailsByBillId(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

/** Get a single client by ID */
export const GET_CLIENT = gql`
  query GetClient($id: ID!) {
    client(id: $id) {
      id
      fName
      lName
      email
      businessName
      phoneNumber
    }
  }
`;

/** Get all clients (for NewBill client selector) */
export const GET_CLIENTS = gql`
  query GetClients {
    clients {
      id
      fName
      lName
      email
      businessName
    }
  }
`;

// ==========================================
// BILL ISSUER QUERIES
// ==========================================

/** Get bill issuer details (authenticated) */
export const GET_BILL_ISSUER = gql`
  query GetBillIssuerDetails($billId: ID!) {
    getBillIssuerDetails(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
      businessRegistrationNumber
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        bankAccounts {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        defaultBankAccountId
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Public bill issuer details (no authentication required) */
export const PUBLIC_GET_BILL_ISSUER = gql`
  query PublicGetBillIssuerDetails($billId: ID!) {
    publicGetBillIssuerDetails(billId: $billId) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
      businessRegistrationNumber
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

// ==========================================
// BILL COMPANY ("BILL FROM") QUERIES
// The registered company (companies module) that issues a bill. When set it is the
// authoritative "Bill From" identity on the invoice/PDF. Readable publicly via the
// bill id (server scopes the lookup to the bill's own tenant — see publicGetBillCompany).
// ==========================================

const BILL_COMPANY_FIELDS = `
  id
  name
  tradingName
  abn
  acn
  email
  phone
  website
  logo
  taxNumber
  billingEmail
  physicalAddress {
    street
    city
    state
    postcode
    country
  }
  primaryContact {
    name
    position
    email
    phone
    mobile
  }
`;

/** Get the issuing company for a bill (authenticated) */
export const GET_BILL_COMPANY = gql`
  query GetBillCompany($billId: ID!) {
    getBillCompany(billId: $billId) {
      ${BILL_COMPANY_FIELDS}
    }
  }
`;

/** Public issuing-company query by bill ID (no authentication required) */
export const PUBLIC_GET_BILL_COMPANY = gql`
  query PublicGetBillCompany($billId: ID!) {
    publicGetBillCompany(billId: $billId) {
      ${BILL_COMPANY_FIELDS}
    }
  }
`;

/** List companies for the admin "Bill From" selector (from the companies module register) */
export const GET_COMPANIES_FOR_BILL = gql`
  query GetCompaniesForBill {
    companies {
      id
      name
      tradingName
      abn
      email
    }
  }
`;

/** Associate (or clear, with null) the issuing company on a bill */
export const SET_BILL_COMPANY = gql`
  mutation SetBillCompany($billId: ID!, $companyId: ID) {
    setBillCompany(billId: $billId, companyId: $companyId) {
      id
      companyId
    }
  }
`;

/** Get the recipient ("Bill To") company for a bill (authenticated) */
export const GET_BILL_TO_COMPANY = gql`
  query GetBillToCompany($billId: ID!) {
    getBillToCompany(billId: $billId) {
      ${BILL_COMPANY_FIELDS}
    }
  }
`;

/** Public recipient ("Bill To") company query by bill ID (no authentication required) */
export const PUBLIC_GET_BILL_TO_COMPANY = gql`
  query PublicGetBillToCompany($billId: ID!) {
    publicGetBillToCompany(billId: $billId) {
      ${BILL_COMPANY_FIELDS}
    }
  }
`;

/** Associate (or clear, with null) the recipient company on a bill */
export const SET_BILL_TO_COMPANY = gql`
  mutation SetBillToCompany($billId: ID!, $companyId: ID) {
    setBillToCompany(billId: $billId, companyId: $companyId) {
      id
      billToCompanyId
    }
  }
`;

// ==========================================
// TENANT QUERIES
// ==========================================

/** Get current tenant with company details (for NewBill tax percentage) */
export const GET_CURRENT_TENANT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        taxPercentage
        billingEmail
        billingPhone
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        bankAccounts {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        defaultBankAccountId
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get current tenant for CompanyDetails page (with invoice preferences) */
export const GET_CURRENT_TENANT_COMPANY = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
        taxPercentage
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
        invoicePreferences {
          emailInvoices
          autoPayEnabled
          invoiceLanguage
          taxIncluded
        }
      }
    }
  }
`;

/** Get current tenant for PaymentReceivingDetails page */
export const GET_CURRENT_TENANT_PAYMENT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        bankAccounts {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        defaultBankAccountId
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get current tenant for EditPaymentDetails page */
export const GET_CURRENT_TENANT_EDIT_PAYMENT = gql`
  query GetCurrentTenant {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        bankAccounts {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        defaultBankAccountId
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get current tenant details for BillDetails page */
export const GET_CURRENT_TENANT_DETAILS = gql`
  query GetCurrentTenantDetails {
    currentTenant {
      id
      name
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        bankAccounts {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        defaultBankAccountId
        cryptoWallets {
          walletAddress
          network
          memo
        }
        paypalEmail
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Get a tenant by ID (for EditPaymentDetails Stripe config) */
export const GET_TENANT = gql`
  query GetTenant($id: ID!) {
    tenant(id: $id) {
      id
      name
      domain
      websiteUrl
      apiKeys {
        stripePublicKey
        stripeSecretKey
        stripeWebhookSecret
      }
      branding {
        siteName
      }
    }
  }
`;

/** Get tenant config for Stripe public key */
export const GET_TENANT_CONFIG = gql`
  query GetTenantConfig($id: ID!) {
    tenant(id: $id) {
      id
      apiKeys {
        stripePublicKey
      }
    }
  }
`;

// ==========================================
// PROJECT QUERIES
// ==========================================

/** Get all projects (for NewBill project selector) */
export const GET_ALL_PROJECTS = gql`
  query GetAllProjects {
    projects {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
    }
  }
`;

/** Get a single project with its tasks (for NewBill line items) */
export const GET_PROJECT_WITH_TASKS = gql`
  query GetProjectWithTasks($projectId: ID!) {
    project(id: $projectId) {
      id
      projectName
      projectGoal
      progress
      billingClient {
        id
        fName
        lName
        email
        businessName
      }
      tasks {
        id
        description
        status
        order
        billed
        assignedTo {
          id
          fName
          lName
        }
        media {
          url
          description
          fileType
        }
      }
    }
  }
`;

// ==========================================
// BILL MUTATIONS
// ==========================================

/** Create a new bill */
export const CREATE_BILL = gql`
  mutation CreateBill($input: BillInput!) {
    createBill(input: $input) {
      id
      isPaid
      status
      paymentMethod
      currency
      acceptCardPayment
      acceptCryptoPayment
      clientId
      lineItems {
        id
        amount
        description
      }
      subtotal
      taxPercentage
      taxAmount
      totalAmount
      projectId
      createdAt
      updatedAt
    }
  }
`;

/** Delete a bill */
export const DELETE_BILL = gql`
  mutation DeleteBill($id: ID!) {
    deleteBill(id: $id)
  }
`;

/** Toggle bill payment status (paid/unpaid) */
export const TOGGLE_BILL_PAYMENT_STATUS = gql`
  mutation ToggleBillPaymentStatus($id: ID!) {
    toggleBillPaymentStatus(id: $id) {
      id
      isPaid
    }
  }
`;

/** Pay a bill "on the house" — issue tokens to the bill's client equal to
 *  the AUD value, then immediately redeem them against the bill. Backend
 *  handles the full transaction; frontend just refetches the bill after.
 *  Gated on REWARDS_REDEEM_ON_BEHALF (or ADMIN / REWARDS_ADMIN). */
export const PAY_BILL_ON_THE_HOUSE = gql`
  mutation PayBillOnTheHouse($billId: ID!) {
    payBillOnTheHouse(billId: $billId)
  }
`;

/** Toggle bill draft status (cycles: PROPOSAL -> DRAFT -> SENT) */
export const TOGGLE_BILL_DRAFT_STATUS = gql`
  mutation ToggleBillDraftStatus($id: ID!) {
    toggleBillDraftStatus(id: $id) {
      id
      status
    }
  }
`;

/** Update bill status with email/SMS notifications */
export const UPDATE_BILL_STATUS = gql`
  mutation UpdateBillStatus($id: ID!, $status: BillStatus!) {
    updateBillStatus(id: $id, status: $status) {
      id
      status
      lineItems {
        id
        description
        amount
      }
      isPaid
      totalAmount
      paymentMethod
      createdAt
      updatedAt
    }
  }
`;

/** Update bill status without sending notifications */
export const UPDATE_BILL_STATUS_WITHOUT_NOTIFICATIONS = gql`
  mutation UpdateBillStatusWithoutNotifications($id: ID!, $status: BillStatus!, $cancellationReason: String) {
    updateBillStatusWithoutNotifications(id: $id, status: $status, cancellationReason: $cancellationReason) {
      id
      status
      cancellationReason
      cancelledAt
      lineItems {
        id
        description
        amount
      }
      isPaid
      totalAmount
      paymentMethod
      createdAt
      updatedAt
    }
  }
`;

/** Update a line item on a bill */
export const UPDATE_LINE_ITEM = gql`
  mutation UpdateLineItem($billId: ID!, $lineItemId: ID!, $amount: Float, $description: String) {
    updateLineItem(
      billId: $billId
      lineItemId: $lineItemId
      amount: $amount
      description: $description
    ) {
      id
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      status
      totalAmount
      isPaid
      updatedAt
    }
  }
`;

/** Delete a line item from a bill */
export const DELETE_LINE_ITEM = gql`
  mutation DeleteLineItem($billId: ID!, $lineItemId: ID!) {
    deleteLineItem(billId: $billId, lineItemId: $lineItemId) {
      id
      lineItems {
        id
        description
        amount
        createdAt
        updatedAt
      }
      status
      totalAmount
      isPaid
      updatedAt
    }
  }
`;

/** Update bill payment method preferences */
export const UPDATE_BILL_PAYMENT_METHODS = gql`
  mutation UpdateBill($id: ID!, $input: BillInput!) {
    updateBill(id: $id, input: $input) {
      id
      acceptBankTransfer
      acceptCardPayment
      acceptCryptoPayment
      acceptTokenPayment
      bankAccountId
    }
  }
`;

/** Duplicate a bill with a percentage of original amounts */
export const DUPLICATE_BILL_WITH_PERCENTAGE = gql`
  mutation DuplicateBillWithPercentage($id: ID!, $percentage: Float!, $paymentTerms: String) {
    duplicateBillWithPercentage(id: $id, percentage: $percentage, paymentTerms: $paymentTerms) {
      id
      isPaid
      status
      currency
      lineItems {
        id
        description
        amount
      }
      totalAmount
      percentageOfTotal
      paymentTerms
    }
  }
`;

/** Change the client associated with a bill.
 *  `alsoUpdateProject` (default false server-side) opts IN to also rewriting the
 *  linked project's billingClient — the old behavior silently always did this.
 *  Throws on locked bills (payment received). */
export const CHANGE_BILL_CLIENT = gql`
  mutation ChangeBillClient($billId: ID!, $newClientId: ID!, $alsoUpdateProject: Boolean) {
    changeBillClient(billId: $billId, newClientId: $newClientId, alsoUpdateProject: $alsoUpdateProject)
  }
`;

/** Inline-edit the bill's client from the Client Information card.
 *  Module-prefixed operation name (codegen uniqueness). PARTIAL-safe: send only
 *  the changed field(s) — undefined fields are never wiped by the server;
 *  explicit ""/null still clears. Server-gated on clients/edit. */
export const BILLS_UPDATE_CLIENT = gql`
  mutation BillsUpdateClient($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      fName
      lName
      email
      phoneNumber
      businessName
    }
  }
`;

/** Import line items from markdown format */
export const IMPORT_LINE_ITEMS_FROM_MARKDOWN = gql`
  mutation ImportLineItemsFromMarkdown($billId: ID!, $markdown: String!) {
    importLineItemsFromMarkdown(billId: $billId, markdown: $markdown) {
      id
      lineItems {
        id
        description
        amount
      }
      totalAmount
      status
      isPaid
      updatedAt
    }
  }
`;

/** Create a bill payment via Stripe */
export const CREATE_BILL_PAYMENT = gql`
  mutation CreateBillPayment($billId: ID!, $paymentMethodId: String!) {
    createBillPayment(billId: $billId, paymentMethodId: $paymentMethodId) {
      success
      requiresAction
      clientSecret
      paymentIntentId
      message
    }
  }
`;

/** Redeem BB Tokens to pay a bill */
export const REDEEM_TOKENS_FOR_BILL = gql`
  mutation RedeemTokensForBill($billId: ID!, $tokenCount: Int!) {
    redeemTokensForBill(billId: $billId, tokenCount: $tokenCount) {
      success
      message
      tokensRedeemed
      tokenValueApplied
      remainingBalance
      billRemainingAmount
    }
  }
`;

// ==========================================
// TENANT MUTATIONS
// ==========================================

/** Update tenant payment receiving details */
export const UPDATE_TENANT_PAYMENT_DETAILS = gql`
  mutation UpdateTenantPaymentDetails($paymentDetails: PaymentReceivingDetailsInput!) {
    updateTenantPaymentDetails(paymentDetails: $paymentDetails) {
      id
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        bankAccounts {
          id
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        defaultBankAccountId
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
        cryptoDiscountPercentage
        acceptTokenPayment
      }
    }
  }
`;

/** Update tenant company details */
export const UPDATE_TENANT_COMPANY_DETAILS = gql`
  mutation UpdateTenantCompanyDetails($companyDetails: TenantCompanyDetailsInput!) {
    updateTenantCompanyDetails(companyDetails: $companyDetails) {
      id
      companyDetails {
        companyName
        taxId
        billingEmail
        billingPhone
        taxPercentage
        billingAddress {
          addressLine1
          addressLine2
          city
          state
          postalCode
          country
        }
        invoicePreferences {
          emailInvoices
          autoPayEnabled
          invoiceLanguage
          taxIncluded
        }
      }
    }
  }
`;

/** Update tenant (for Stripe API keys) */
export const UPDATE_TENANT = gql`
  mutation UpdateTenant($id: ID!, $input: UpdateTenantInput!) {
    updateTenant(id: $id, input: $input) {
      id
      apiKeys {
        stripePublicKey
        stripeSecretKey
        stripeWebhookSecret
      }
    }
  }
`;

// ==========================================
// BANK TRANSFER PAYMENT MUTATIONS
// ==========================================

export const SUBMIT_BANK_TRANSFER_PAYMENT = gql`
  mutation SubmitBankTransferPayment($billId: ID!, $amount: Float!, $screenshotUrl: String) {
    submitBankTransferPayment(billId: $billId, amount: $amount, screenshotUrl: $screenshotUrl)
  }
`;

/** PUBLIC bank transfer submission — a client following a shared bill link can record a
 *  bank transfer WITHOUT logging in (Stripe/crypto still require auth). */
export const PUBLIC_SUBMIT_BANK_TRANSFER_PAYMENT = gql`
  mutation PublicSubmitBankTransferPayment($billId: ID!, $amount: Float!, $screenshotUrl: String) {
    publicSubmitBankTransferPayment(billId: $billId, amount: $amount, screenshotUrl: $screenshotUrl)
  }
`;

export const CONFIRM_BANK_TRANSFER_PAYMENT = gql`
  mutation ConfirmBankTransferPayment($billId: ID!, $paymentEventIndex: Int!) {
    confirmBankTransferPayment(billId: $billId, paymentEventIndex: $paymentEventIndex)
  }
`;

export const RESET_PAYMENT_EVENT = gql`
  mutation ResetPaymentEvent($billId: ID!, $paymentEventIndex: Int!) {
    resetPaymentEvent(billId: $billId, paymentEventIndex: $paymentEventIndex)
  }
`;

// ==========================================
// BILL NOTIFICATION RULE QUERIES / MUTATIONS
// ==========================================

const BILL_NOTIFICATION_RULE_FIELDS = `
  id
  name
  trigger
  recipientType
  recipientIds
  recipientTag
  channels
  timing
  timingMinutes
  isEnabled
  messageTemplate
  createdAt
`;

/** Get all bill notification rules */
export const GET_BILL_NOTIFICATION_RULES = gql`
  query GetBillNotificationRules {
    billNotificationRules {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Create a bill notification rule */
export const CREATE_BILL_NOTIFICATION_RULE = gql`
  mutation CreateBillNotificationRule($input: CreateBillNotificationRuleInput!) {
    createBillNotificationRule(input: $input) {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Update a bill notification rule */
export const UPDATE_BILL_NOTIFICATION_RULE = gql`
  mutation UpdateBillNotificationRule($input: UpdateBillNotificationRuleInput!) {
    updateBillNotificationRule(input: $input) {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Delete a bill notification rule */
export const DELETE_BILL_NOTIFICATION_RULE = gql`
  mutation DeleteBillNotificationRule($id: ID!) {
    deleteBillNotificationRule(id: $id)
  }
`;

/** Toggle a bill notification rule enabled/disabled */
export const TOGGLE_BILL_NOTIFICATION_RULE = gql`
  mutation ToggleBillNotificationRule($id: ID!) {
    toggleBillNotificationRule(id: $id) {
      ${BILL_NOTIFICATION_RULE_FIELDS}
    }
  }
`;

/** Search clients for notification rule recipients */
export const SEARCH_CLIENTS_FOR_NOTIFICATIONS = gql`
  query SearchClientsForNotifications {
    clients {
      id
      fName
      lName
      email
    }
  }
`;

// ==========================================
// EMAIL BRANDING QUERIES (per-module override of tenant.branding for Bills emails)
// ==========================================

const BILLS_EMAIL_BRANDING_FIELDS = `
  id
  penName
  emailFooterText
  profilePhotoCid
  signatureImageCid
  bannerImageCid
  profilePhotoUrl
  signatureImageUrl
  bannerImageUrl
  socialLinks { linkedin instagram facebook twitter youtube website }
  ctaButtonText
  ctaButtonUrl
  ctaButtonStyle
  emailStyle
  emailFont
  primaryColor
  secondaryColor
  accentColor
  profilePhotoSize
  socialIconStyle
  hideTagline
  hideSiteName
  previewSampleBody
  createdAt
  updatedAt
`;

export const GET_BILLS_EMAIL_BRANDING = gql`
  query GetBillsEmailBranding {
    billsEmailBranding {
      ${BILLS_EMAIL_BRANDING_FIELDS}
    }
  }
`;

export const UPSERT_BILLS_EMAIL_BRANDING = gql`
  mutation UpsertBillsEmailBranding($input: BillsEmailBrandingInput!) {
    upsertBillsEmailBranding(input: $input) {
      ${BILLS_EMAIL_BRANDING_FIELDS}
    }
  }
`;

export const PREVIEW_BILLS_EMAIL_BRANDING = gql`
  query PreviewBillsEmailBranding($input: BillsEmailBrandingInput!, $sampleBody: String) {
    previewBillsEmailBranding(input: $input, sampleBody: $sampleBody)
  }
`;

export const SEND_TEST_BILLS_EMAIL = gql`
  mutation SendTestBillsEmail($html: String!, $subject: String) {
    sendTestBillsEmail(html: $html, subject: $subject)
  }
`;

// ============================================================
// COUPONS (admin) — added 2026-06-12
// ============================================================

export const GET_COUPONS = gql`
  query GetCoupons {
    coupons {
      id code description discountType discountValue appliesToProductIds
      maxRedemptions perClientLimit redemptionCount expiresAt isActive createdAt
    }
  }
`;

export const CREATE_COUPON = gql`
  mutation CreateCoupon($input: CreateCouponInput!) {
    createCoupon(input: $input) { id code }
  }
`;

export const UPDATE_COUPON = gql`
  mutation UpdateCoupon($id: ID!, $input: UpdateCouponInput!) {
    updateCoupon(id: $id, input: $input) { id code isActive }
  }
`;

export const DEACTIVATE_COUPON = gql`
  mutation DeactivateCoupon($id: ID!) {
    deactivateCoupon(id: $id)
  }
`;

// ============================================================
// BB-token payment — reward balance + token config for BBTokenPayment.tsx.
// Module-local copies; do NOT import rewards' queries
// (feedback_module_independence — the bills module must build on tenants
// whose rewards copy is stale or absent).
// ============================================================

export const GET_PUBLIC_REWARD_TOKEN_CONFIG = gql`
  query BillsPublicRewardTokenConfig {
    publicRewardTokenConfig {
      id
      tokenName
      tokenNamePlural
      tokenIcon
      tokenColor
      tokenValue
      defaultExpiryMonths
      isActive
    }
  }
`;

export const GET_MY_REWARD_BALANCE = gql`
  query BillsMyRewardBalance {
    myRewardBalance {
      clientId
      clientName
      activeTokens
      totalValue
      expiringWithin30Days
    }
  }
`;

// Minimal LM enrollments — SubscribePage shows which subscription products the
// user is already enrolled in. Module-local copy; do NOT import lm's queries
// (feedback_module_independence).
export const GET_MY_ENROLLMENTS_MINIMAL = gql`
    query BillsGetMyEnrollmentsMinimal {
        lmGetMyEnrollments {
            id
            courseId
        }
    }
`;

// The invoice PDF, rendered by the BACKEND (bills module `billPdf`). The
// renderer moved server-side on 2026-08-06 so that ONE engine produces the
// invoice for every consumer — this page's download, its preview, and the
// Knowledge Explorer chat's attach-to-draft-email tool. A browser-side copy
// could drift from the emailed copy with nobody noticing.
// Two variants, same trust model as the bill queries above them:
//   BILL_PDF        — authed, gated by bills view permission
//   PUBLIC_BILL_PDF — the logged-out /bill/:id page, bill id as the token
export const BILL_PDF = gql`
    query BillPdf($id: ID!) {
        billPdf(id: $id) {
            filename
            contentBase64
            contentLength
        }
    }
`;

export const PUBLIC_BILL_PDF = gql`
    query PublicBillPdf($id: ID!) {
        publicBillPdf(id: $id) {
            filename
            contentBase64
            contentLength
        }
    }
`;

// ==========================================
// REPORTING & WEEKLY SNAPSHOT
// ==========================================

/**
 * Every metric carries the stored fields it came from AND the date field that
 * bucketed it into the period — the tiles render `source` verbatim underneath
 * the figure. `available: false` means the data cannot support the metric, and
 * the tile must show `unavailableReason` instead of the zero.
 */
const REPORT_METRIC_FIELDS = `
  key
  label
  count
  amount
  source
  available
  unavailableReason
`;

/** Bills activity over a Brisbane calendar period, optionally split per issuer brand. */
export const GET_BILLS_REPORT = gql`
  query GetBillsReport($input: BillsReportInput!) {
    billsReport(input: $input) {
      periodStart
      periodEnd
      periodStartDay
      periodEndDay
      timeZone
      generatedAt
      currency
      notes
      metrics {
        ${REPORT_METRIC_FIELDS}
      }
      byBrand {
        brandId
        brandName
        source
        metrics {
          ${REPORT_METRIC_FIELDS}
        }
      }
    }
  }
`;

/**
 * Reading this runs the demand-driven catch-up first, so any complete week with
 * no row yet is written before the list comes back. There is no cron — that is
 * the design, not an omission.
 */
export const GET_BILLS_WEEKLY_SNAPSHOTS = gql`
  query GetBillsWeeklySnapshots($limit: Int) {
    billsWeeklySnapshots(limit: $limit) {
      id
      weekStartDay
      weekEndDay
      isoWeek
      periodStart
      periodEnd
      timeZone
      generatedAt
      generatedBy
      generatedAutomatically
      notes
      metrics {
        ${REPORT_METRIC_FIELDS}
      }
    }
  }
`;

/** Take (or retake) the snapshot for one complete Brisbane week. Idempotent per week. */
export const GENERATE_BILLS_WEEKLY_SNAPSHOT = gql`
  mutation GenerateBillsWeeklySnapshot($weekStartDay: String) {
    generateBillsWeeklySnapshot(weekStartDay: $weekStartDay) {
      id
      weekStartDay
      weekEndDay
      isoWeek
      generatedAt
      generatedAutomatically
      notes
      metrics {
        ${REPORT_METRIC_FIELDS}
      }
    }
  }
`;

export const GET_BILLS_REPORTING_SETTINGS = gql`
  query GetBillsReportingSettings {
    billsReportingSettings {
      id
      weeklySnapshotEnabled
      snapshotBackfillWeeks
      timeZone
      lastSnapshotAt
    }
  }
`;

export const UPDATE_BILLS_REPORTING_SETTINGS = gql`
  mutation UpdateBillsReportingSettings($input: BillsReportingSettingsInput!) {
    updateBillsReportingSettings(input: $input) {
      id
      weeklySnapshotEnabled
      snapshotBackfillWeeks
      timeZone
      lastSnapshotAt
    }
  }
`;

// ===== Payment terms + due date (2026-10-05) =====

export const BILLS_SETTINGS = gql`
  query BillsSettings {
    billsSettings {
      id
      defaultPaymentTerms
      defaultDueDays
      updatedByName
      updatedAt
    }
  }
`;

export const UPDATE_BILLS_SETTINGS = gql`
  mutation UpdateBillsSettings($input: BillsSettingsInput!) {
    updateBillsSettings(input: $input) {
      id
      defaultPaymentTerms
      defaultDueDays
      updatedByName
      updatedAt
    }
  }
`;

export const SET_BILL_TERMS = gql`
  mutation SetBillTerms($id: ID!, $paymentTerms: String, $dueDate: String) {
    setBillTerms(id: $id, paymentTerms: $paymentTerms, dueDate: $dueDate) {
      id
      paymentTerms
      dueDate
      terms {
        paymentTerms
        paymentTermsSource
        dueDate
        dueDateSource
        dueDaysAfterSending
      }
    }
  }
`;
